import { getPoolPreview } from '../data/pools.js';
import { getOptions, setOptions } from '../runtime.js';
import { hasReviewableData } from '../storage.js';
import type { Country, PoolKey, QuizMode, RuntimeOptions } from '../types.js';

export interface StartMenuHandlers {
  onStart: () => void;
  onOpenStats: () => void;
}

interface PoolOption {
  key: PoolKey;
  label: string;
}

const POOL_OPTIONS: readonly PoolOption[] = [
  { key: 'review', label: '복습 ⭐' },
  { key: 'easy', label: '쉬운 30개' },
  { key: 'East-South Asia', label: '동·남아시아' },
  { key: 'West-Central Asia', label: '서·중앙아시아' },
  { key: 'Europe', label: '유럽' },
  { key: 'Africa', label: '아프리카' },
  { key: 'North America', label: '북아메리카' },
  { key: 'South America', label: '남아메리카' },
  { key: 'Oceania', label: '오세아니아' },
];

const QUIZ_MODES: readonly { key: QuizMode; label: string }[] = [
  { key: 'name', label: '이름 맞히기' },
  { key: 'location', label: '위치 맞히기' },
  { key: 'mixed', label: '섞기 🔀' },
];

export class StartMenu {
  private readonly root: HTMLElement;
  private readonly countries: readonly Country[];
  private handlers: StartMenuHandlers | null = null;
  private current: RuntimeOptions;

  constructor(root: HTMLElement, countries: readonly Country[]) {
    this.root = root;
    this.countries = countries;
    this.current = { ...getOptions() };
    this.root.innerHTML = `
      <div class="start-menu" hidden>
        <div class="start-card">
          <h1 class="start-title">나라 맞히기</h1>
          <p class="start-sub">출제 풀과 방식을 골라봐</p>
          <div class="start-group">
            <div class="start-label">출제 풀</div>
            <div class="start-options" data-group="pool"></div>
            <div class="start-pool-preview" hidden></div>
          </div>
          <div class="start-group">
            <div class="start-label">출제 방식</div>
            <div class="start-options" data-group="mode"></div>
          </div>
          <button type="button" class="start-button">시작!</button>
          <button type="button" class="start-stats-button">내 기록</button>
        </div>
      </div>
    `;
    this.renderOptions();
    const startBtn = this.must<HTMLButtonElement>('.start-button');
    startBtn.addEventListener('click', () => {
      setOptions(this.current);
      this.hide();
      this.handlers?.onStart();
    });
    const statsBtn = this.must<HTMLButtonElement>('.start-stats-button');
    statsBtn.addEventListener('click', () => {
      this.handlers?.onOpenStats();
    });
  }

  setHandlers(h: StartMenuHandlers): void {
    this.handlers = h;
  }

  show(): void {
    this.current = { ...getOptions() };
    // 복습이 막혀 있는데 직전 선택이 'review' 였다면 'easy'로 되돌림
    // — 비활성 칩이 active 표시되는 상태를 방지.
    if (this.current.pool === 'review' && !hasReviewableData()) {
      this.current = { ...this.current, pool: 'easy' };
    }
    this.renderOptions();
    this.must<HTMLElement>('.start-menu').hidden = false;
  }

  hide(): void {
    this.must<HTMLElement>('.start-menu').hidden = true;
  }

  private renderOptions(): void {
    const reviewable = hasReviewableData();
    const poolGroup = this.must<HTMLElement>('[data-group="pool"]');
    poolGroup.innerHTML = '';
    for (const opt of POOL_OPTIONS) {
      const disabled = opt.key === 'review' && !reviewable;
      const label = disabled ? `${opt.label} (1라운드 끝나면 열려!)` : opt.label;
      const btn = this.makeOptionButton(label, opt.key === this.current.pool, disabled);
      if (!disabled) {
        btn.addEventListener('click', () => {
          this.current = { ...this.current, pool: opt.key };
          this.renderOptions();
        });
      }
      poolGroup.appendChild(btn);
    }

    this.renderPoolPreview();

    const modeGroup = this.must<HTMLElement>('[data-group="mode"]');
    modeGroup.innerHTML = '';
    for (const opt of QUIZ_MODES) {
      const btn = this.makeOptionButton(opt.label, opt.key === this.current.quizMode, false);
      btn.addEventListener('click', () => {
        this.current = { ...this.current, quizMode: opt.key };
        this.renderOptions();
      });
      modeGroup.appendChild(btn);
    }
  }

  private renderPoolPreview(): void {
    const previewEl = this.must<HTMLElement>('.start-pool-preview');
    const preview = getPoolPreview(this.current.pool, this.countries, 5);
    if (preview === null) {
      previewEl.hidden = true;
      previewEl.textContent = '';
      return;
    }
    previewEl.hidden = false;
    previewEl.textContent = `총 ${preview.total}개 · ${preview.samples.join(', ')}${
      preview.samples.length < preview.total ? ', …' : ''
    }`;
  }

  private makeOptionButton(label: string, active: boolean, disabled: boolean): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `start-opt${active ? ' active' : ''}`;
    btn.textContent = label;
    if (disabled) btn.disabled = true;
    return btn;
  }

  private must<T extends Element = HTMLElement>(selector: string): T {
    const el = this.root.querySelector<T>(selector);
    if (!el) throw new Error(`StartMenu 요소 누락: ${selector}`);
    return el;
  }
}
